export const SESSION_STATEMENT_SELECTOR_KEYS = [
    "schemaVersion",
    "licenseId",
    "hostId",
    "sessionId",
];
export const SESSION_STATEMENT_REQUEST_KEYS = [
    "schemaVersion",
    "sessionId",
    "clientNonce",
    "bindingKind",
];
export const SESSION_STATEMENT_ENVELOPE_KEYS = [
    "schemaVersion",
    "payload",
    "sig",
];
export const SESSION_CONFIRMATION_REPORT_KEYS = [
    "schemaVersion",
    "sessionId",
    "clientNonce",
    "confirmationCode",
];
export const SESSION_CONFIRMATION_RESULT_KEYS = [
    "schemaVersion",
    "sessionId",
    "clientNonce",
    "outcome",
];
export const SESSION_VERIFICATION_BINDING_KINDS = [
    "wss-webpki-v1",
    "webrtc-dtls-v1",
];
export const SESSION_CONFIRMATION_OUTCOMES = [
    "matched",
    "mismatched",
    "unenforced-no-surface",
    "report-invalid",
    "report-timeout",
];
const UUID_V4_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/u;
const HOST_ID_PATTERN = /^[0-9A-HJKMNP-TV-Z]{8}$/u;
const CLIENT_NONCE_PATTERN = /^[A-Za-z0-9_-]{21}[AQgw]$/u;
const SIGNATURE_PATTERN = /^[A-Za-z0-9_-]{85}[AQgw]$/u;
const BASE64URL_PATTERN = /^[A-Za-z0-9_-]+$/u;
const CONFIRMATION_CODE_PATTERN = /^[0-9A-HJKMNP-TV-Z]{3}-[0-9A-HJKMNP-TV-Z]{3}$/u;
const BASE64URL_ALPHABET = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_";
function hasOwn(value, key) {
    return Object.prototype.hasOwnProperty.call(value, key);
}
function isRecord(value) {
    return typeof value === "object" && value !== null && !Array.isArray(value);
}
function exactKeys(value, expected, label, ordered) {
    const actual = Object.keys(value);
    const keysMatch = ordered
        ? actual.every((key, index) => key === expected[index])
        : expected.every((key) => hasOwn(value, key));
    if (actual.length !== expected.length || !keysMatch) {
        throw new TypeError(`${label} keys are not exact`);
    }
}
function record(value, expected, label, ordered) {
    if (!isRecord(value))
        throw new TypeError(`${label} must be an object`);
    exactKeys(value, expected, label, ordered);
    return value;
}
function literalOne(value, label) {
    if (value !== 1)
        throw new TypeError(`${label} must be 1`);
}
function pattern(value, expected, label) {
    if (typeof value !== "string" || !expected.test(value)) {
        throw new TypeError(`${label} is invalid`);
    }
    return value;
}
function canonicalBase64Url(value, label) {
    const encoded = pattern(value, BASE64URL_PATTERN, label);
    const remainder = encoded.length % 4;
    const finalIndex = BASE64URL_ALPHABET.indexOf(encoded.length === 0 ? "" : (encoded[encoded.length - 1] ?? ""));
    if (remainder === 1 ||
        (remainder === 2 && (finalIndex & 0x0f) !== 0) ||
        (remainder === 3 && (finalIndex & 0x03) !== 0)) {
        throw new TypeError(`${label} is not canonical base64url`);
    }
    return encoded;
}
function oneOf(value, values, label) {
    if (typeof value !== "string" || !values.includes(value)) {
        throw new TypeError(`${label} is invalid`);
    }
    return value;
}
function continuation(value) {
    return value !== undefined && value >= 0x80 && value <= 0xbf;
}
/** Strict UTF-8 decoder kept independent of Node and DOM runtime types. */
function decodeUtf8(bytes) {
    if (bytes[0] === 0xef && bytes[1] === 0xbb && bytes[2] === 0xbf) {
        throw new TypeError("W7 logical body must not carry a UTF-8 BOM");
    }
    let text = "";
    for (let index = 0; index < bytes.length;) {
        const first = bytes[index];
        if (first === undefined)
            break;
        if (first <= 0x7f) {
            text += String.fromCodePoint(first);
            index += 1;
            continue;
        }
        const second = bytes[index + 1];
        if (first >= 0xc2 && first <= 0xdf && continuation(second)) {
            text += String.fromCodePoint(((first & 0x1f) << 6) | (second & 0x3f));
            index += 2;
            continue;
        }
        const third = bytes[index + 2];
        if (continuation(second) &&
            continuation(third) &&
            ((first === 0xe0 && second >= 0xa0) ||
                (first >= 0xe1 && first <= 0xec) ||
                (first === 0xed && second <= 0x9f) ||
                (first >= 0xee && first <= 0xef))) {
            text += String.fromCodePoint(((first & 0x0f) << 12) | ((second & 0x3f) << 6) | (third & 0x3f));
            index += 3;
            continue;
        }
        const fourth = bytes[index + 3];
        if (continuation(second) &&
            continuation(third) &&
            continuation(fourth) &&
            ((first === 0xf0 && second >= 0x90) ||
                (first >= 0xf1 && first <= 0xf3) ||
                (first === 0xf4 && second <= 0x8f))) {
            text += String.fromCodePoint(((first & 0x07) << 18) |
                ((second & 0x3f) << 12) |
                ((third & 0x3f) << 6) |
                (fourth & 0x3f));
            index += 4;
            continue;
        }
        throw new TypeError("W7 logical body is not valid UTF-8");
    }
    return text;
}
/** UTF-8 encoder kept independent of Node and DOM runtime types. */
function encodeUtf8(value) {
    const bytes = [];
    for (const character of value) {
        const point = character.codePointAt(0);
        if (point === undefined || (point >= 0xd800 && point <= 0xdfff)) {
            throw new TypeError("W7 logical body contains an invalid scalar value");
        }
        if (point <= 0x7f)
            bytes.push(point);
        else if (point <= 0x7ff) {
            bytes.push(0xc0 | (point >>> 6), 0x80 | (point & 0x3f));
        }
        else if (point <= 0xffff) {
            bytes.push(0xe0 | (point >>> 12), 0x80 | ((point >>> 6) & 0x3f), 0x80 | (point & 0x3f));
        }
        else {
            bytes.push(0xf0 | (point >>> 18), 0x80 | ((point >>> 12) & 0x3f), 0x80 | ((point >>> 6) & 0x3f), 0x80 | (point & 0x3f));
        }
    }
    return Uint8Array.from(bytes);
}
function decodeExactRecord(bytes, expectedKeys, label, validate) {
    if (!(bytes instanceof Uint8Array) || bytes.byteLength === 0) {
        throw new TypeError(`${label} must be non-empty bytes`);
    }
    const source = decodeUtf8(bytes);
    let parsed;
    try {
        parsed = JSON.parse(source);
    }
    catch (error) {
        throw new TypeError(`${label} is not JSON`, { cause: error });
    }
    const value = record(parsed, expectedKeys, label, true);
    if (JSON.stringify(value) !== source) {
        throw new TypeError(`${label} is not canonical compact JSON`);
    }
    return Object.freeze(validate(value, true));
}
function encodeExactRecord(value, label, validate, orderedValue) {
    const validated = validate(value, false);
    const source = JSON.stringify(orderedValue(validated));
    const bytes = encodeUtf8(source);
    return Uint8Array.from(bytes);
}
function selector(value, ordered) {
    const item = record(value, SESSION_STATEMENT_SELECTOR_KEYS, "session-statement-selector", ordered);
    literalOne(item.schemaVersion, "selector schemaVersion");
    return {
        schemaVersion: 1,
        licenseId: pattern(item.licenseId, UUID_V4_PATTERN, "selector licenseId"),
        hostId: pattern(item.hostId, HOST_ID_PATTERN, "selector hostId"),
        sessionId: pattern(item.sessionId, UUID_V4_PATTERN, "selector sessionId"),
    };
}
function request(value, ordered) {
    const item = record(value, SESSION_STATEMENT_REQUEST_KEYS, "session-statement-request", ordered);
    literalOne(item.schemaVersion, "request schemaVersion");
    return {
        schemaVersion: 1,
        sessionId: pattern(item.sessionId, UUID_V4_PATTERN, "request sessionId"),
        clientNonce: pattern(item.clientNonce, CLIENT_NONCE_PATTERN, "request clientNonce"),
        bindingKind: oneOf(item.bindingKind, SESSION_VERIFICATION_BINDING_KINDS, "request bindingKind"),
    };
}
function statement(value, ordered) {
    const item = record(value, SESSION_STATEMENT_ENVELOPE_KEYS, "session-statement", ordered);
    literalOne(item.schemaVersion, "statement schemaVersion");
    return {
        schemaVersion: 1,
        payload: canonicalBase64Url(item.payload, "statement payload"),
        sig: pattern(item.sig, SIGNATURE_PATTERN, "statement signature"),
    };
}
function report(value, ordered) {
    const item = record(value, SESSION_CONFIRMATION_REPORT_KEYS, "session-confirmation-report", ordered);
    literalOne(item.schemaVersion, "report schemaVersion");
    return {
        schemaVersion: 1,
        sessionId: pattern(item.sessionId, UUID_V4_PATTERN, "report sessionId"),
        clientNonce: pattern(item.clientNonce, CLIENT_NONCE_PATTERN, "report clientNonce"),
        confirmationCode: pattern(item.confirmationCode, CONFIRMATION_CODE_PATTERN, "report confirmationCode"),
    };
}
function result(value, ordered) {
    const item = record(value, SESSION_CONFIRMATION_RESULT_KEYS, "session-confirmation-result", ordered);
    literalOne(item.schemaVersion, "result schemaVersion");
    return {
        schemaVersion: 1,
        sessionId: pattern(item.sessionId, UUID_V4_PATTERN, "result sessionId"),
        clientNonce: pattern(item.clientNonce, CLIENT_NONCE_PATTERN, "result clientNonce"),
        outcome: oneOf(item.outcome, SESSION_CONFIRMATION_OUTCOMES, "result outcome"),
    };
}
export function isSessionStatementSelectorV1(value) {
    try {
        selector(value, true);
        return true;
    }
    catch {
        return false;
    }
}
export function isSessionStatementRequestV1(value) {
    try {
        request(value, true);
        return true;
    }
    catch {
        return false;
    }
}
export function isSessionStatementEnvelopeV1(value) {
    try {
        statement(value, true);
        return true;
    }
    catch {
        return false;
    }
}
export function isSessionConfirmationReportV1(value) {
    try {
        report(value, true);
        return true;
    }
    catch {
        return false;
    }
}
export function isSessionConfirmationResultV1(value) {
    try {
        result(value, true);
        return true;
    }
    catch {
        return false;
    }
}
export const decodeSessionStatementSelectorV1 = (bytes) => decodeExactRecord(bytes, SESSION_STATEMENT_SELECTOR_KEYS, "session-statement-selector", selector);
export const decodeSessionStatementRequestV1 = (bytes) => decodeExactRecord(bytes, SESSION_STATEMENT_REQUEST_KEYS, "session-statement-request", request);
export const decodeSessionStatementEnvelopeV1 = (bytes) => decodeExactRecord(bytes, SESSION_STATEMENT_ENVELOPE_KEYS, "session-statement", statement);
export const decodeSessionConfirmationReportV1 = (bytes) => decodeExactRecord(bytes, SESSION_CONFIRMATION_REPORT_KEYS, "session-confirmation-report", report);
export const decodeSessionConfirmationResultV1 = (bytes) => decodeExactRecord(bytes, SESSION_CONFIRMATION_RESULT_KEYS, "session-confirmation-result", result);
export const encodeSessionStatementSelectorV1 = (value) => encodeExactRecord(value, "session-statement-selector", selector, (item) => ({
    schemaVersion: item.schemaVersion,
    licenseId: item.licenseId,
    hostId: item.hostId,
    sessionId: item.sessionId,
}));
export const encodeSessionStatementRequestV1 = (value) => encodeExactRecord(value, "session-statement-request", request, (item) => ({
    schemaVersion: item.schemaVersion,
    sessionId: item.sessionId,
    clientNonce: item.clientNonce,
    bindingKind: item.bindingKind,
}));
export const encodeSessionStatementEnvelopeV1 = (value) => encodeExactRecord(value, "session-statement", statement, (item) => ({
    schemaVersion: item.schemaVersion,
    payload: item.payload,
    sig: item.sig,
}));
export const encodeSessionConfirmationReportV1 = (value) => encodeExactRecord(value, "session-confirmation-report", report, (item) => ({
    schemaVersion: item.schemaVersion,
    sessionId: item.sessionId,
    clientNonce: item.clientNonce,
    confirmationCode: item.confirmationCode,
}));
export const encodeSessionConfirmationResultV1 = (value) => encodeExactRecord(value, "session-confirmation-result", result, (item) => ({
    schemaVersion: item.schemaVersion,
    sessionId: item.sessionId,
    clientNonce: item.clientNonce,
    outcome: item.outcome,
}));
export const SESSION_BOARD_KEY_RECOVERY_DISPOSITION_MAP = Object.freeze({
    retry: Object.freeze({ kind: "unavailable", diagnostic: "signer-retry" }),
    "key-recovery": Object.freeze({
        kind: "recovery-required",
        diagnostic: "key-recovery",
    }),
    "operator-recovery": Object.freeze({
        kind: "recovery-required",
        diagnostic: "operator-recovery",
    }),
});
export const SESSION_STATEMENT_PRODUCTION_PUBLIC_CODE_MAP = Object.freeze({
    "signer-retry": "INTERNAL",
    "carrier-unavailable": null,
    "key-recovery": "SESSION_STATEMENT_INVALID",
    "operator-recovery": "SESSION_STATEMENT_INVALID",
    "request-invalid": "SESSION_STATEMENT_INVALID",
    "selector-mismatch": "SESSION_STATEMENT_INVALID",
    "binding-mismatch": "SESSION_STATEMENT_INVALID",
    "pin-ineligible": "SESSION_STATEMENT_INVALID",
});
export const SESSION_VERIFICATION_PUBLIC_CODE_MAP = Object.freeze({
    "anchor-unavailable": "BOOTSTRAP_UNAVAILABLE",
    "anchor-timeout": "BOOTSTRAP_TIMEOUT",
    "anchor-absent": "SESSION_STATEMENT_INVALID",
    "anchor-invalid": "SESSION_STATEMENT_INVALID",
    "selector-unavailable": "W7_RESPONSE_TIMEOUT",
    "selector-timeout": "W7_RESPONSE_TIMEOUT",
    "selector-invalid": "SESSION_STATEMENT_INVALID",
    "selector-mismatch": "SESSION_MISMATCH",
    "statement-unavailable": "W7_RESPONSE_TIMEOUT",
    "statement-timeout": "W7_RESPONSE_TIMEOUT",
    "statement-malformed": "SESSION_STATEMENT_INVALID",
    "unsupported-schema": "SESSION_STATEMENT_INVALID",
    "role-mismatch": "SESSION_STATEMENT_INVALID",
    "identity-mismatch": "SESSION_STATEMENT_INVALID",
    "session-mismatch": "SESSION_MISMATCH",
    "challenge-mismatch": "SESSION_STATEMENT_INVALID",
    "challenge-replayed": "SESSION_STATEMENT_INVALID",
    "key-mismatch": "SESSION_STATEMENT_INVALID",
    "binding-mismatch": "SESSION_STATEMENT_INVALID",
    "time-invalid": "SESSION_STATEMENT_INVALID",
    "signature-invalid": "SESSION_STATEMENT_INVALID",
    "verifier-unavailable": "INTERNAL",
    "producer-unavailable": "INTERNAL",
    "producer-invalid": "SESSION_STATEMENT_INVALID",
});
/**
 * W11 cut-1 browser-facing readers. They are deliberately co-located with the
 * existing dormant W7 codecs because R2 admits this exact protocol path but no
 * new protocol sibling. These functions only decode; no W11 producer imports
 * or calls them before the later activation cuts.
 */
const W11_DEVICE_ID_PATTERN = /^[A-Za-z0-9_-]{43}$/u;
const W11_REGISTRATION_NONCE_PATTERN = /^[A-Za-z0-9_-]{22}$/u;
const W11_DEVICE_LABEL_FORBIDDEN = new RegExp(String.raw `[\u0000-\u001f\u007f\u061c\u200e\u200f\u2028-\u202e\u2066-\u2069]`, "u");
function w11FreezeJson(value) {
    if (value !== null && typeof value === "object" && !Object.isFrozen(value)) {
        for (const child of Object.values(value))
            w11FreezeJson(child);
        Object.freeze(value);
    }
    return value;
}
function w11ExactObject(bytes, label) {
    if (!(bytes instanceof Uint8Array) || bytes.byteLength === 0) {
        throw new TypeError(`${label} must be non-empty bytes`);
    }
    const source = decodeUtf8(bytes);
    let parsed;
    try {
        parsed = JSON.parse(source);
    }
    catch (error) {
        throw new TypeError(`${label} is not JSON`, { cause: error });
    }
    if (!isRecord(parsed) || JSON.stringify(parsed) !== source) {
        throw new TypeError(`${label} is not canonical compact JSON`);
    }
    return parsed;
}
function w11Record(value, label) {
    if (!isRecord(value))
        throw new TypeError(`${label} must be an object`);
    return value;
}
function w11ExactKeys(value, expected, label) {
    exactKeys(value, expected, label, true);
}
function w11PositiveSafeInteger(value, label) {
    if (!Number.isSafeInteger(value) || value <= 0) {
        throw new TypeError(`${label} is invalid`);
    }
    return value;
}
function w11CanonicalBase64UrlBytes(value, patternValue, decodedBytes, label) {
    const encoded = pattern(value, patternValue, label);
    canonicalBase64Url(encoded, label);
    if (Math.floor((encoded.length * 6) / 8) !== decodedBytes) {
        throw new TypeError(`${label} decoded length is invalid`);
    }
    return encoded;
}
function w11DeviceId(value, label) {
    return w11CanonicalBase64UrlBytes(value, W11_DEVICE_ID_PATTERN, 32, label);
}
function w11RegistrationNonce(value, label) {
    return w11CanonicalBase64UrlBytes(value, W11_REGISTRATION_NONCE_PATTERN, 16, label);
}
function w11Label(value, label) {
    if (typeof value !== "string" ||
        value.normalize("NFC") !== value ||
        [...value].length < 1 ||
        [...value].length > 64 ||
        W11_DEVICE_LABEL_FORBIDDEN.test(value) ||
        decodeUtf8(encodeUtf8(value)) !== value) {
        throw new TypeError(`${label} is invalid`);
    }
    return value;
}
const W11_CLAIM_READERS = {
    "unregistered-v1": (value, label) => {
        w11ExactKeys(value, ["kind", "registrationNonce", "deviceLabel", "admissionIntent"], label);
        w11RegistrationNonce(value.registrationNonce, `${label}.registrationNonce`);
        w11Label(value.deviceLabel, `${label}.deviceLabel`);
        oneOf(value.admissionIntent, ["join-open", "request-registration"], `${label}.admissionIntent`);
        return value;
    },
    "trusted-v1": (value, label) => {
        w11ExactKeys(value, ["kind", "deviceId", "credentialRevision", "deviceToken"], label);
        w11DeviceId(value.deviceId, `${label}.deviceId`);
        w11PositiveSafeInteger(value.credentialRevision, `${label}.credentialRevision`);
        w11DeviceId(value.deviceToken, `${label}.deviceToken`);
        return value;
    },
    "delivery-recovery-v1": (value, label) => {
        w11ExactKeys(value, [
            "kind",
            "deviceId",
            "credentialRevision",
            "deviceToken",
            "deliveryNonce",
        ], label);
        w11DeviceId(value.deviceId, `${label}.deviceId`);
        w11PositiveSafeInteger(value.credentialRevision, `${label}.credentialRevision`);
        w11DeviceId(value.deviceToken, `${label}.deviceToken`);
        w11DeviceId(value.deliveryNonce, `${label}.deliveryNonce`);
        return value;
    },
    "rotation-recovery-v1": (value, label) => {
        w11ExactKeys(value, [
            "kind",
            "deviceId",
            "credentialRevision",
            "deviceToken",
            "nextCredentialRevision",
            "nextDeviceToken",
            "deliveryNonce",
        ], label);
        w11DeviceId(value.deviceId, `${label}.deviceId`);
        const current = w11PositiveSafeInteger(value.credentialRevision, `${label}.credentialRevision`);
        w11DeviceId(value.deviceToken, `${label}.deviceToken`);
        const next = w11PositiveSafeInteger(value.nextCredentialRevision, `${label}.nextCredentialRevision`);
        if (next <= current) {
            throw new TypeError(`${label}.nextCredentialRevision is invalid`);
        }
        w11DeviceId(value.nextDeviceToken, `${label}.nextDeviceToken`);
        w11DeviceId(value.deliveryNonce, `${label}.deliveryNonce`);
        return value;
    },
};
const W11_CLAIM_KINDS = Object.freeze(Object.keys(W11_CLAIM_READERS));
export function readW11DeviceClaimV1(value, label = "W11 device claim") {
    const item = w11Record(value, label);
    const kind = oneOf(item.kind, W11_CLAIM_KINDS, `${label}.kind`);
    const reader = W11_CLAIM_READERS[kind];
    return w11FreezeJson(reader(item, label));
}
export function parseW11DeviceClaimV1(bytes) {
    return readW11DeviceClaimV1(w11ExactObject(bytes, "W11 device claim"));
}
/** Reads the optional member without closing or changing the v1 join object. */
export function readOptionalW11DeviceClaimV1(joinValue) {
    const join = w11Record(joinValue, "join payload");
    if (!hasOwn(join, "deviceClaim"))
        return Object.freeze({ kind: "absent" });
    return Object.freeze({
        kind: "present",
        claim: readW11DeviceClaimV1(join.deviceClaim),
    });
}
function readW11StorageCommon(value, label) {
    literalOne(value.schemaVersion, `${label}.schemaVersion`);
    pattern(value.hostId, HOST_ID_PATTERN, `${label}.hostId`);
    w11DeviceId(value.deviceId, `${label}.deviceId`);
    const revision = w11PositiveSafeInteger(value.credentialRevision, `${label}.credentialRevision`);
    w11DeviceId(value.deviceToken, `${label}.deviceToken`);
    return revision;
}
const W11_STORAGE_READERS = {
    "pending-first": (value, label) => {
        w11ExactKeys(value, [
            "storageState",
            "schemaVersion",
            "hostId",
            "deviceId",
            "credentialRevision",
            "deviceToken",
            "deliveryNonce",
        ], label);
        readW11StorageCommon(value, label);
        w11DeviceId(value.deliveryNonce, `${label}.deliveryNonce`);
        return value;
    },
    stable: (value, label) => {
        w11ExactKeys(value, [
            "storageState",
            "schemaVersion",
            "hostId",
            "deviceId",
            "credentialRevision",
            "deviceToken",
        ], label);
        readW11StorageCommon(value, label);
        return value;
    },
    rotating: (value, label) => {
        w11ExactKeys(value, [
            "storageState",
            "schemaVersion",
            "hostId",
            "deviceId",
            "credentialRevision",
            "deviceToken",
            "nextCredentialRevision",
            "nextDeviceToken",
            "deliveryNonce",
        ], label);
        const current = readW11StorageCommon(value, label);
        const next = w11PositiveSafeInteger(value.nextCredentialRevision, `${label}.nextCredentialRevision`);
        if (next <= current) {
            throw new TypeError(`${label}.nextCredentialRevision is invalid`);
        }
        w11DeviceId(value.nextDeviceToken, `${label}.nextDeviceToken`);
        w11DeviceId(value.deliveryNonce, `${label}.deliveryNonce`);
        return value;
    },
};
const W11_STORAGE_STATES = Object.freeze(Object.keys(W11_STORAGE_READERS));
export function parseW11BrowserStorageRecordV1(bytes) {
    const item = w11ExactObject(bytes, "W11 browser storage record");
    const state = oneOf(item.storageState, W11_STORAGE_STATES, "W11 browser storage record.storageState");
    const reader = W11_STORAGE_READERS[state];
    return w11FreezeJson(reader(item, "W11 browser storage record"));
}
export const W11_BROWSER_STORAGE_READ_DISPOSITIONS = Object.freeze([
    "absent",
    "pending-first",
    "stable",
    "rotating",
    "recovery-required/storage-invalid",
    "recovery-required/host-mismatch",
    "recovery-required/storage-busy",
    "recovery-required/storage-unavailable",
    "recovery-required/readback-mismatch",
]);
export function readW11BrowserStorageDispositionV1(value) {
    return oneOf(value, W11_BROWSER_STORAGE_READ_DISPOSITIONS, "W11 browser storage disposition");
}
const W11_DELIVERY_READERS = {
    "authorisation-member-ready-ack": (value, label) => {
        const payload = w11Record(value.payload, `${label}.payload`);
        w11ExactKeys(payload, ["schemaVersion"], `${label}.payload`);
        literalOne(payload.schemaVersion, `${label}.payload.schemaVersion`);
        return value;
    },
    "authorisation-device-credential": (value, label) => {
        const payload = w11Record(value.payload, `${label}.payload`);
        w11ExactKeys(payload, [
            "schemaVersion",
            "purpose",
            "hostId",
            "deviceId",
            "credentialRevision",
            "deviceToken",
            "deliveryNonce",
        ], `${label}.payload`);
        literalOne(payload.schemaVersion, `${label}.payload.schemaVersion`);
        oneOf(payload.purpose, ["registration", "rotation"], `${label}.payload.purpose`);
        pattern(payload.hostId, HOST_ID_PATTERN, `${label}.payload.hostId`);
        w11DeviceId(payload.deviceId, `${label}.payload.deviceId`);
        w11PositiveSafeInteger(payload.credentialRevision, `${label}.payload.credentialRevision`);
        w11DeviceId(payload.deviceToken, `${label}.payload.deviceToken`);
        w11DeviceId(payload.deliveryNonce, `${label}.payload.deliveryNonce`);
        return value;
    },
    "authorisation-device-credential-ack": (value, label) => {
        const payload = w11Record(value.payload, `${label}.payload`);
        w11ExactKeys(payload, ["schemaVersion", "deviceId", "credentialRevision", "deliveryNonce"], `${label}.payload`);
        literalOne(payload.schemaVersion, `${label}.payload.schemaVersion`);
        w11DeviceId(payload.deviceId, `${label}.payload.deviceId`);
        w11PositiveSafeInteger(payload.credentialRevision, `${label}.payload.credentialRevision`);
        w11DeviceId(payload.deliveryNonce, `${label}.payload.deliveryNonce`);
        return value;
    },
    "authorisation-device-credential-result": (value, label) => {
        const payload = w11Record(value.payload, `${label}.payload`);
        w11ExactKeys(payload, ["schemaVersion", "deviceId", "credentialRevision", "outcome"], `${label}.payload`);
        literalOne(payload.schemaVersion, `${label}.payload.schemaVersion`);
        w11DeviceId(payload.deviceId, `${label}.payload.deviceId`);
        w11PositiveSafeInteger(payload.credentialRevision, `${label}.payload.credentialRevision`);
        oneOf(payload.outcome, ["activated", "already-activated", "rejected"], `${label}.payload.outcome`);
        return value;
    },
};
const W11_DELIVERY_TYPES = Object.freeze(Object.keys(W11_DELIVERY_READERS));
export function parseW11DeliveryLogicalRecordV1(bytes) {
    const item = w11ExactObject(bytes, "W11 delivery logical record");
    w11ExactKeys(item, ["type", "payload"], "W11 delivery logical record");
    const type = oneOf(item.type, W11_DELIVERY_TYPES, "W11 delivery logical record.type");
    const reader = W11_DELIVERY_READERS[type];
    return w11FreezeJson(reader(item, "W11 delivery logical record"));
}
export const AUTHORISATION_CUT1_BROWSER_READERS = Object.freeze({
    parseW11DeviceClaimV1,
    readOptionalW11DeviceClaimV1,
    parseW11BrowserStorageRecordV1,
    readW11BrowserStorageDispositionV1,
    parseW11DeliveryLogicalRecordV1,
});
// --- W7 wire message schemas and the v1 message unions -----------------------
//
// Moved here from schemas.ts so that module no longer imports this one: the
// production renderer bundles schemas.js for the W10 data-channel helpers, and
// an import edge from there would drag every session-verification field list
// (including the clientNonce literal) into the renderer bundle, which
// assert-production-renderer forbids. The unions live beside the W7 payload
// schemas because they are the only values that reference them.
import { z } from "zod";
import { anyRtcSignalMessageSchema, clientRtcSignalMessageSchema, debugForceTransportMessageSchema, errorMessageSchema, hostMediaMessageSchema, joinAckMessageSchema, joinMessageSchema, kickMessageSchema, leaveMessageSchema, legCloseMessageSchema, legCreateMessageSchema, legStatusMessageSchema, peerJoinedMessageSchema, peerLeftMessageSchema, peerUpdateMessageSchema, peerUpdatedMessageSchema, pingMessageSchema, pongMessageSchema, roomStateMessageSchema, rtcSignalMessageSchema, shareChangedMessageSchema, shareDecisionMessageSchema, shareReadyMessageSchema, shareRequestMessageSchema, shareRevokeMessageSchema, shareStopMessageSchema, statsReportMessageSchema, syncRequestMessageSchema, transportChangedMessageSchema, v1MessageSchema, } from "./schemas.js";
const exactW7PayloadSchema = (guard, label) => z.custom(guard, {
    message: `${label} payload must have exact fields in exact order`,
});
export const sessionStatementSelectorPayloadSchema = exactW7PayloadSchema(isSessionStatementSelectorV1, "session-statement-selector");
export const sessionStatementRequestPayloadSchema = exactW7PayloadSchema(isSessionStatementRequestV1, "session-statement-request");
export const sessionStatementPayloadSchema = exactW7PayloadSchema(isSessionStatementEnvelopeV1, "session-statement");
export const sessionConfirmationReportPayloadSchema = exactW7PayloadSchema(isSessionConfirmationReportV1, "session-confirmation-report");
export const sessionConfirmationResultPayloadSchema = exactW7PayloadSchema(isSessionConfirmationResultV1, "session-confirmation-result");
export const sessionStatementSelectorMessageSchema = v1MessageSchema("session-statement-selector", sessionStatementSelectorPayloadSchema);
export const sessionStatementRequestMessageSchema = v1MessageSchema("session-statement-request", sessionStatementRequestPayloadSchema);
export const sessionStatementMessageSchema = v1MessageSchema("session-statement", sessionStatementPayloadSchema);
export const sessionConfirmationReportMessageSchema = v1MessageSchema("session-confirmation-report", sessionConfirmationReportPayloadSchema);
export const sessionConfirmationResultMessageSchema = v1MessageSchema("session-confirmation-result", sessionConfirmationResultPayloadSchema);
/** Messages accepted from an authenticated or pre-join browser client. */
export const clientMessageSchema = z.discriminatedUnion("type", [
    sessionStatementRequestMessageSchema,
    sessionConfirmationReportMessageSchema,
    joinMessageSchema,
    leaveMessageSchema,
    pingMessageSchema,
    syncRequestMessageSchema,
    peerUpdateMessageSchema,
    shareRequestMessageSchema,
    shareReadyMessageSchema,
    shareStopMessageSchema,
    legStatusMessageSchema,
    clientRtcSignalMessageSchema,
    statsReportMessageSchema,
    errorMessageSchema,
]);
/** Messages emitted by the signaling host toward browser clients. */
export const serverMessageSchema = z.discriminatedUnion("type", [
    sessionStatementSelectorMessageSchema,
    sessionStatementMessageSchema,
    sessionConfirmationResultMessageSchema,
    joinAckMessageSchema,
    kickMessageSchema,
    pongMessageSchema,
    roomStateMessageSchema,
    peerJoinedMessageSchema,
    peerLeftMessageSchema,
    peerUpdatedMessageSchema,
    shareDecisionMessageSchema,
    shareRevokeMessageSchema,
    shareChangedMessageSchema,
    hostMediaMessageSchema,
    legCreateMessageSchema,
    legCloseMessageSchema,
    transportChangedMessageSchema,
    rtcSignalMessageSchema,
    errorMessageSchema,
]);
export const outboundMessageSchema = serverMessageSchema;
/** Every known v1 message, including the internal debug-only command. */
export const allMessageSchema = z.discriminatedUnion("type", [
    sessionStatementSelectorMessageSchema,
    sessionStatementRequestMessageSchema,
    sessionStatementMessageSchema,
    sessionConfirmationReportMessageSchema,
    sessionConfirmationResultMessageSchema,
    joinMessageSchema,
    joinAckMessageSchema,
    leaveMessageSchema,
    kickMessageSchema,
    pingMessageSchema,
    pongMessageSchema,
    roomStateMessageSchema,
    syncRequestMessageSchema,
    peerJoinedMessageSchema,
    peerLeftMessageSchema,
    peerUpdatedMessageSchema,
    peerUpdateMessageSchema,
    shareRequestMessageSchema,
    shareDecisionMessageSchema,
    shareReadyMessageSchema,
    shareStopMessageSchema,
    shareRevokeMessageSchema,
    shareChangedMessageSchema,
    hostMediaMessageSchema,
    legCreateMessageSchema,
    legCloseMessageSchema,
    legStatusMessageSchema,
    transportChangedMessageSchema,
    anyRtcSignalMessageSchema,
    statsReportMessageSchema,
    errorMessageSchema,
    debugForceTransportMessageSchema,
]);
//# sourceMappingURL=session-verification.js.map